import React, { useContext, useState, useEffect, useCallback } from "react";
import { assets } from "../assets/assets";
import { Link, NavLink } from "react-router-dom";
import { ShopContext } from "../context/ShopContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from "@/components/ui/dialog";
import { FaArrowRight } from "react-icons/fa";

const Navbar = () => {
  const [visible, setVisible] = useState(false);
  const { setShowSearch, getCartCount, navigate, token, setToken, setCartItems } = useContext(ShopContext);
  const [googleProfilePic, setGoogleProfilePic] = useState(null);
  const [avatarLoading, setAvatarLoading] = useState(false);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    const googleAuth = JSON.parse(localStorage.getItem("googleAuth"));
    if (user && googleAuth) {
      setAvatarLoading(true);
      const img = new window.Image();
      img.src = user.avatar;
      img.onload = () => {
        setGoogleProfilePic(user.avatar);
        setAvatarLoading(false);
      };
      img.onerror = () => {
        setGoogleProfilePic(null);
        setAvatarLoading(false);
      };
    } else {
      setGoogleProfilePic(null);
      setAvatarLoading(false);
    }
  }, [token]);


  const logout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("googleAuth");
    setToken("");
    setCartItems({});
    navigate("/login");
  }, [navigate, setToken, setCartItems]);

  const handleSearchClick = useCallback(() => {
    setShowSearch(true);
    navigate('/collection');
  }, [setShowSearch, navigate]);

  return (
    <div className="flex items-center justify-between py-5 font-medium relative">
      <Link to="/">
        <img className="w-36" src={assets.logo} alt="Logo" />
      </Link>

      {/* Desktop Links */}
      <ul className="hidden sm:flex gap-5 text-sm text-gray-700">
        <NavLink to="/" className="flex flex-col items-center gap-1">
          <p>HOME</p>
          <hr className="w-2/4 border-none h-[1.5px] bg-gray-700 hidden" />
        </NavLink>
        <NavLink to="/collection" className="flex flex-col items-center gap-1">
          <p>CATALOGUE</p>
          <hr className="w-2/4 border-none h-[1.5px] bg-gray-700 hidden" />
        </NavLink>
        <NavLink to="/about" className="flex flex-col items-center gap-1">
          <p>ABOUT</p>
          <hr className="w-2/4 border-none h-[1.5px] bg-gray-700 hidden" />
        </NavLink>
        <NavLink to="/contact" className="flex flex-col items-center gap-1">
          <p>CONTACT</p>
          <hr className="w-2/4 border-none h-[1.5px] bg-gray-700 hidden" />
        </NavLink>
      </ul>

      {/* Right Side */}
      <div className="inline-flex items-center gap-6 sm:p-0">
        
        {/* Search Icon */}
        <button
          onClick={handleSearchClick}
          className="p-1 ml-8 sm:ml-0"
        >
          <img src={assets.search_icon} className="w-5" alt="Search" />
        </button>

        {/* If logged in */}
        {token ? (
          <>
            {/* Profile */}
            <div className="group relative">
              <Dialog>
                <DialogTrigger className="group relative">
                  {avatarLoading ? (
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-gray-200 to-gray-400 flex items-center justify-center animate-pulse">
                      <svg className="w-4 h-4 text-gray-500 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                      </svg>
                    </div>
                  ) : googleProfilePic ? (
                    <img
                      src={googleProfilePic}
                      className="w-6 h-6 rounded-full object-cover cursor-pointer mt-1"
                      alt="Profile"
                      onError={e => { e.target.onerror = null; e.target.src = assets.profile_icon; }}
                    />
                  ) : (
                    <img
                      src={assets.profile_icon}
                      className="w-5 cursor-pointer mt-2"
                      alt="Profile"
                    />
                  )}
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>User Menu</DialogTitle>
                    <DialogDescription>
                      <hr className="p-2" />
                      <DialogClose>
                        <p
                          onClick={() => navigate("/user/account/settings")}
                          className="cursor-pointer hover:text-black"
                        >
                          My Account
                        </p>
                      </DialogClose>
                      <hr className="p-2" />
                      <DialogClose>
                        <p
                          onClick={() => navigate("/orders")}
                          className="cursor-pointer hover:text-black"
                        >
                          Orders
                        </p>
                      </DialogClose>
                      <hr className="p-2" />
                      <p
                        onClick={logout}
                        className="cursor-pointer hover:text-black"
                      >
                        Logout
                      </p>
                      <hr className="p-2" />
                    </DialogDescription>
                  </DialogHeader>
                </DialogContent>
              </Dialog>
            </div>

            {/* Cart */}
            <Link to="/cart" className="relative">
              <img
                className="w-5 min-w-5 sm:p-0 sm:gap-2"
                src={assets.cart_icon}
                alt="Cart"
              />
              <p className="absolute right-[-5px] bottom-[-5px] w-4 text-center leading-4 bg-black text-white aspect-square rounded-full text-[8px]">
                {getCartCount()}
              </p>
            </Link>
          </>
        ) : (
          // If not logged in - Show Login Button
          <Link to="/login">
            <button className="border border-black text-black px-4 py-1 rounded-full flex items-center gap-2 transition-all hover:bg-black hover:text-white">
              <span>Log In</span>
              <FaArrowRight />
            </button>
          </Link>
        )}

        {/* Mobile Hamburger Menu */}
        <img
          onClick={() => setVisible(true)}
          src={assets.menu_icon}
          className="w-5 cursor-pointer sm:hidden"
          alt="Menu"
        />
      </div>

      {/* Sidebar for Mobile */}
      <div
        className={`fixed top-0 right-0 bottom-0 z-50 bg-white overflow-hidden transition-all duration-300 ${visible ? "w-64" : "w-0"}`}
      >
        <div className="flex flex-col text-gray-600 h-full">
          <div
            onClick={() => setVisible(false)}
            className="flex items-center gap-4 p-3 border-b"
          >
            <img className="h-4 rotate-180" src={assets.dropdown_icon} alt="" />
            <p>Back</p>
          </div>
          <NavLink
            onClick={() => setVisible(false)}
            className="py-3 pl-6 border-b hover:bg-gray-100"
            to="/"
          >
            HOME
          </NavLink>
          <NavLink
            onClick={() => setVisible(false)}
            className="py-3 pl-6 border-b hover:bg-gray-100"
            to="/collection"
          >
            CATALOGUE
          </NavLink>
          <NavLink
            onClick={() => setVisible(false)}
            className="py-3 pl-6 border-b hover:bg-gray-100"
            to="/about"
          >
            ABOUT
          </NavLink>
          <NavLink
            onClick={() => setVisible(false)}
            className="py-3 pl-6 border-b hover:bg-gray-100"
            to="/contact"
          >
            CONTACT
          </NavLink>
        </div>
      </div>
    </div>
  );
};

export default Navbar;
